import { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Monitor, X, Plus, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const inputCls = "h-10 bg-transparent border-white/10 text-white text-sm placeholder:text-white/25";
const emptyForm = {
  name: "", objective: "SEARCH", daily_budget: "10", start_date: "", end_date: "",
  locations: [], language: "it", keywords: [], negative_keywords: [],
};
const LANGS = [["it", "Italiano"], ["en", "Inglese"], ["de", "Tedesco"], ["fr", "Francese"], ["es", "Spagnolo"]];
const MATCH_LABELS = { BROAD: "Generica", PHRASE: "Frase", EXACT: "Esatta" };
const STEPS = ["Obiettivo", "Budget e date", "Targeting", "Parole chiave", "Riepilogo"];

const Chip = ({ label, onRemove, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-white/5 border border-white/15 text-xs text-white/80">
    {label}
    <button type="button" onClick={onRemove} className="text-white/40 hover:text-red-400 transition-colors"><X size={11} /></button>
  </span>
);

export const AdsCampaignWizard = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [locInput, setLocInput] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [kwMatch, setKwMatch] = useState("BROAD");
  const [negInput, setNegInput] = useState("");
  const [creating, setCreating] = useState(false);

  const reset = () => { setStep(0); setForm(emptyForm); setLocInput(""); setKwInput(""); setNegInput(""); };
  const close = () => { onClose(); reset(); };

  const addLocation = () => {
    const v = locInput.trim();
    if (form.locations.length >= 20) { toast.error("Massimo 20 località"); return; }
    if (v && !form.locations.includes(v)) setForm({ ...form, locations: [...form.locations, v] });
    setLocInput("");
  };
  const addKeyword = () => {
    const v = kwInput.trim();
    if (v && !form.keywords.some((k) => k.text === v)) setForm({ ...form, keywords: [...form.keywords, { text: v, match_type: kwMatch }] });
    setKwInput("");
  };
  const addNegative = () => {
    const v = negInput.trim();
    if (v && !form.negative_keywords.includes(v)) setForm({ ...form, negative_keywords: [...form.negative_keywords, v] });
    setNegInput("");
  };

  const canNext = step === 0 ? form.name.trim().length > 0 : step === 1 ? parseFloat(form.daily_budget) > 0 : true;

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/ads/campaigns", {
        ...form, name: form.name.trim(), daily_budget: parseFloat(form.daily_budget),
      });
      toast.success(data.message, { duration: 8000 });
      onCreated?.();
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ads-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl">Nuova campagna Google Ads</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Passo {step + 1} di {STEPS.length} — {STEPS[step]}. La campagna viene creata <span className="text-amber-300">in pausa</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 mb-1" data-testid="ads-wizard-progress">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-blue-400" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="ads-wizard-step-objective">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Nome campagna</Label>
              <Input required data-testid="wizard-name-input" value={form.name} autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. Promozione autunno" className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Obiettivo</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["SEARCH", "Rete di Ricerca", "Annunci di testo nei risultati Google", MagnifyingGlass],
                  ["DISPLAY", "Rete Display", "Banner su siti e app partner", Monitor]].map(([val, label, desc, Icon]) => (
                  <button key={val} type="button" data-testid={`wizard-objective-${val.toLowerCase()}`}
                    onClick={() => setForm({ ...form, objective: val })}
                    className={`text-left p-4 rounded-lg border transition-colors ${form.objective === val ? "border-blue-400/60 bg-blue-500/10" : "border-white/10 hover:border-white/25"}`}>
                    <Icon size={20} weight="duotone" className={form.objective === val ? "text-blue-300" : "text-white/40"} />
                    <p className="text-sm font-semibold mt-2">{label}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">{desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="ads-wizard-step-budget">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Budget giornaliero (€)</Label>
              <Input required type="number" min="0.01" step="0.01" data-testid="wizard-budget-input"
                value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Data inizio (facoltativa)</Label>
                <Input type="date" data-testid="wizard-start-date-input" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Data fine (facoltativa)</Label>
                <Input type="date" data-testid="wizard-end-date-input" value={form.end_date} min={form.start_date || undefined}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
              </div>
            </div>
            <p className="text-[11px] text-white/35">Senza data inizio la campagna parte quando la attivi; senza data fine resta attiva finché non la fermi.</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="ads-wizard-step-targeting">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Aree geografiche</Label>
              <div className="flex gap-2">
                <Input data-testid="wizard-location-input" value={locInput} onChange={(e) => setLocInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLocation())}
                  placeholder="Es. Milano, Lombardia, Italia..." className={inputCls} />
                <Button type="button" onClick={addLocation} data-testid="wizard-add-location-button"
                  className="h-10 px-4 bg-white/10 text-white hover:bg-white/20 rounded-full text-xs font-semibold shrink-0">
                  <Plus size={13} className="mr-1" /> Aggiungi
                </Button>
              </div>
              {form.locations.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {form.locations.map((l) => (
                    <Chip key={l} label={l} testId={`wizard-location-${l}`}
                      onRemove={() => setForm({ ...form, locations: form.locations.filter((x) => x !== l) })} />
                  ))}
                </div>
              )}
              <p className="text-[11px] text-white/35">Scrivi città, province, regioni o paesi (in italiano, max 20). Se vuota, la campagna non avrà restrizioni geografiche.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Lingua del pubblico</Label>
              <div className="flex flex-wrap gap-2">
                {LANGS.map(([val, label]) => (
                  <button key={val} type="button" data-testid={`wizard-lang-${val}`}
                    onClick={() => setForm({ ...form, language: val })}
                    className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${form.language === val ? "bg-blue-500/15 text-blue-300 border-blue-400/50" : "border-white/15 text-white/50 hover:text-white"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="ads-wizard-step-keywords">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Parole chiave</Label>
              <div className="flex gap-2">
                <Input data-testid="wizard-keyword-input" value={kwInput} onChange={(e) => setKwInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                  placeholder="Es. ristorante milano centro" className={inputCls} />
                <select data-testid="wizard-keyword-match-select" value={kwMatch} onChange={(e) => setKwMatch(e.target.value)}
                  className="h-10 px-2 rounded-md bg-surface border border-white/10 text-white text-xs shrink-0">
                  {Object.entries(MATCH_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Button type="button" onClick={addKeyword} data-testid="wizard-add-keyword-button"
                  className="h-10 px-3 bg-white/10 text-white hover:bg-white/20 rounded-full text-xs font-semibold shrink-0">
                  <Plus size={13} />
                </Button>
              </div>
              {form.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {form.keywords.map((k) => (
                    <Chip key={k.text} label={`${k.text} · ${MATCH_LABELS[k.match_type]}`} testId={`wizard-keyword-${k.text}`}
                      onRemove={() => setForm({ ...form, keywords: form.keywords.filter((x) => x.text !== k.text) })} />
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Parole chiave negative (escludono ricerche)</Label>
              <div className="flex gap-2">
                <Input data-testid="wizard-negative-input" value={negInput} onChange={(e) => setNegInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNegative())}
                  placeholder="Es. gratis, lavoro..." className={inputCls} />
                <Button type="button" onClick={addNegative} data-testid="wizard-add-negative-button"
                  className="h-10 px-3 bg-white/10 text-white hover:bg-white/20 rounded-full text-xs font-semibold shrink-0">
                  <Plus size={13} />
                </Button>
              </div>
              {form.negative_keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {form.negative_keywords.map((n) => (
                    <Chip key={n} label={n} testId={`wizard-negative-${n}`}
                      onRemove={() => setForm({ ...form, negative_keywords: form.negative_keywords.filter((x) => x !== n) })} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-2 text-sm" data-testid="ads-wizard-step-summary">
            {[["Nome", form.name],
              ["Obiettivo", form.objective === "SEARCH" ? "Rete di Ricerca" : "Rete Display"],
              ["Budget", `€ ${parseFloat(form.daily_budget || 0).toFixed(2)}/giorno`],
              ["Periodo", form.start_date || form.end_date ? `${form.start_date || "subito"} → ${form.end_date || "senza fine"}` : "Dall'attivazione, senza fine"],
              ["Aree geografiche", form.locations.length ? form.locations.join(", ") : "Nessuna restrizione"],
              ["Lingua", LANGS.find(([v]) => v === form.language)?.[1]],
              ["Parole chiave", form.keywords.length ? form.keywords.map((k) => k.text).join(", ") : "Nessuna (aggiungile dopo)"],
              ["Negative", form.negative_keywords.length ? form.negative_keywords.join(", ") : "Nessuna"],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-3 py-1.5 border-b border-white/5">
                <span className="w-36 shrink-0 text-[11px] tracking-[0.15em] uppercase text-white/40 pt-0.5">{label}</span>
                <span className="text-white/85">{value}</span>
              </div>
            ))}
            <p className="text-[11px] text-amber-300/80 pt-2">La campagna viene creata in pausa con un gruppo annunci: completa gli annunci su Google Ads, poi attivala da qui.</p>
          </div>
        )}

        <div className="flex justify-between gap-3 pt-2">
          <Button type="button" variant="ghost" onClick={() => (step === 0 ? close() : setStep(step - 1))}
            data-testid="wizard-back-button"
            className="h-10 px-5 border border-white/15 text-white/60 hover:text-white hover:bg-white/5 rounded-full text-sm font-semibold">
            {step === 0 ? "Annulla" : <><CaretLeft size={13} className="mr-1" /> Indietro</>}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={() => setStep(step + 1)} disabled={!canNext} data-testid="wizard-next-button"
              className="h-10 px-6 bg-white text-black hover:bg-gray-200 rounded-full text-sm font-semibold disabled:opacity-40">
              Avanti <CaretRight size={13} className="ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={handleCreate} disabled={creating} data-testid="wizard-create-button"
              className="h-10 px-6 bg-blue-500 text-white hover:bg-blue-400 rounded-full text-sm font-semibold">
              {creating ? "Creazione..." : "Crea campagna (in pausa)"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
